import type { ProjectElementNode } from '@deltek/specification-client/core/Models/productListingDetail'
import { IconButton, makeStyles } from '@material-ui/core'
import AddCircleIcon from '@material-ui/icons/AddCircle'
import { useElementItemShallowSelector } from 'components/WorkspaceEditor/ElementItemStore'
import React, { useCallback, useState } from 'react'
import AddManufacturerPopup from './AddManufacturerPopup'
import { useAddCustomManufacturer } from './hooks'

const ADD_ICON_COLOR = '#0D7B55'

const useStyles = makeStyles((theme) => ({
    container: {
        marginLeft: theme.spacing(4.5),
        width: '100%',
    },
    addButton: {
        padding: theme.spacing(0.5),
    },
}))

interface AddManufacturerButtonProps {
    selectedProjectElementNode?: Partial<ProjectElementNode>
    isBOD?: boolean
    /** Root article to refetch after insert so the new pill's optimistic
     * pillId is reconciled with the server's real one. */
    rootArticleId?: string
}

export const AddManufacturerButton: React.FC<AddManufacturerButtonProps> = ({
    selectedProjectElementNode,
    isBOD = false,
    rootArticleId,
}) => {
    const classes = useStyles()
    const [isOpen, setIsOpen] = useState(false)

    const elementId = selectedProjectElementNode?.elementId ?? ''
    const projectId = selectedProjectElementNode?.projectId ?? ''

    const { parentFamilyId } = useElementItemShallowSelector((s) => ({
        parentFamilyId: s.elementItemValue.parentFamilyId,
    }))

    const productFamilyId =
        selectedProjectElementNode?.elementType === 'PRODUCTTYPE'
            ? parentFamilyId ?? ''
            : selectedProjectElementNode?.elementId ?? ''

    const { addCustomManufacturer, localProductListings } = useAddCustomManufacturer({
        isBOD,
        productFamilyId,
        projectId,
        elementId,
        rootArticleId,
    })

    const handleButtonClick = () => {
        setIsOpen((prev) => !prev)
    }

    const handleClose = () => {
        setIsOpen(false)
    }

    const handleManufacturerAdded = useCallback(
        (manufacturerName: string) => {
            addCustomManufacturer(manufacturerName)
            setIsOpen(false)
        },
        [addCustomManufacturer]
    )

    return (
        <div className={classes.container}>
            <IconButton
                aria-label="add-manufacturer-popup-button"
                data-testid="add-manufacturer-popup-button"
                onClick={handleButtonClick}
                className={classes.addButton}
                size="small"
            >
                <AddCircleIcon htmlColor={ADD_ICON_COLOR} />
            </IconButton>
            {isOpen && (
                <AddManufacturerPopup
                    closePopup={handleClose}
                    elementId={elementId}
                    productFamilyId={productFamilyId}
                    projectId={projectId}
                    localProductListings={localProductListings}
                    onManufacturerAdded={handleManufacturerAdded}
                />
            )}
        </div>
    )
}
